import Input from '@material-ui/core/Input';
import { makeStyles } from '@material-ui/core/styles';
import { Theme } from '@material-ui/core/styles/createTheme';
import Typography from '@material-ui/core/Typography';
import CheckCircleIcon from '@material-ui/icons/CheckCircle';
import CloudDownloadIcon from '@material-ui/icons/CloudDownload';
import * as PlaceImage from '@material-ui/icons/Image';
import WarningIcon from '@material-ui/icons/Warning';
import { useCallback, useEffect, useState } from 'react';
import { DropzoneInputProps, DropzoneOptions, DropzoneRootProps, FileRejection, useDropzone } from 'react-dropzone';
import { FieldValues } from 'react-hook-form';
import { Placeholder } from '../components/Editable/Placeholder';
import { ImageButtonIcon } from '../components/SvgComponents/SvgImageButton';
import { useAppSelector } from '../store/hooks';
import { _focusedItemName } from '../store/slices/editorSlice';
import { colors } from '../theme/palette';
import {
	HEADER_LOGO,
	ACCEPTABLE_IMAGE_MIME_TYPES,
	IMAGE_BANNER,
	IMAGE_BUTTON,
	IMAGE_CONTENT,
	IMAGE_PREVIEW,
	TOPIC_BACKGROUND,
	ACCEPTABLE_IMAGE_FILE_EXTENSIONS,
	MAX_IMAGE_UPLOAD_SIZE,
} from '../util/constants';

// *************************************************************
// Styling
// *************************************************************

const useStyles = makeStyles<
	Theme,
	{ backgroundColor?: string; borderColor?: string; width?: string; height?: string }
>({
	bannerImageOptionContent: {
		height: '100%',
		width: '100%',
		maxWidth: '100%',
	},
	imageOptionContent: {
		width: 'auto',
		maxWidth: '100%',
	},
	logoImageOptionContent: {
		width: '150px',
		height: '150px',
		maxWidth: '150px',
	},

	dropZone: (props) => ({
		backgroundColor: props.backgroundColor,
		borderColor: props.borderColor,
		display: 'flex',
		width: '100%',
		height: props.height,
		alignItems: 'center',
		justifyContent: 'center',
		flexDirection: 'column',
		overflow: 'hidden',
	}),
	dragState: (props) => ({
		backgroundColor: props.backgroundColor,
		borderColor: props.borderColor,
		borderWidth: 2,
		borderStyle: 'dashed',
		display: 'flex',
		width: '100%',
		height: props.height,
		alignItems: 'center',
		justifyContent: 'center',
		flexDirection: 'column',
		overflow: 'hidden',
	}),
	successUpload: (props) => ({
		backgroundColor: props.backgroundColor,
		borderColor: colors.greenDark,
		borderWidth: 2,
		borderStyle: 'dashed',
		display: 'flex',
		width: '100%',
		height: 200,
		alignItems: 'center',
		justifyContent: 'center',
		flexDirection: 'column',
		overflow: 'hidden',
	}),
	icon: (props) => ({
		width: 50,
		height: 50,
		color: props.borderColor,
	}),
	failureText: {
		color: colors.redDark,
		textAlign: 'center',
	},
	errorMessage: {
		textAlign: 'center',
	},
	wrapper: {
		'&:after': {
			content: "''",
			position: 'absolute',
			top: 0,
			left: 0,
			width: '100%',
			height: '100%',
		},
	},
});

// *************************************************************
// Helper Functions
// *************************************************************

const getImageInfo = async (file: File) => {
	return new Promise<ImageInfo>((resolve) => {
		let reader = new FileReader();
		reader.onload = () => {
			const img = new Image();
			img.onload = () => {
				resolve({
					width: img.width,
					height: img.height,
					sizeInBytes: file.size,
				});
			};
			img.src = reader.result as string;
		};

		reader.readAsDataURL(file);
	});
};

// *************************************************************
// Types
// *************************************************************

export type ImageInfo = {
	width: number;
	height: number;
	sizeInBytes: number;
};

/**
 * A validation function that should return either an error message or null (if all validation rules pass successfully)
 */
export type ImageValidator = (imageInfo: ImageInfo) => string | null;

// Hook props
export type UseImageUploadFieldProps<TFieldValues extends FieldValues = FieldValues> = {
	initialValue?: string;
	fieldName: Extract<keyof TFieldValues, string>;
	fieldLabel?: string;
	acceptedFileTypes?: DropzoneOptions['accept'];
	maxFileSizeBytes?: number;
	setDirtiness?: (fieldName: keyof TFieldValues, isDirty: boolean) => void;
	validateImage?: ImageValidator;
	setImagePreview: (file: File) => void;
	isDrag?: boolean;
	imageType?: string;
	isOver?: boolean;
	isValidType?: boolean;
};

type ImageUploadFieldProps = {
	getRootProps: (props?: DropzoneRootProps) => DropzoneRootProps;
	getInputProps: (props?: DropzoneInputProps) => DropzoneInputProps;
	fileTypes: string;
	imagePreviewUrl: string;
	isDragActive: boolean;
	success: boolean;
	failure: boolean;
	name: string;
	label: string;
	helperText: string;
	errorMessage: string;
	fieldRequirementsText?: string;
	isDrag?: boolean;
	imageType?: string;
	isOver?: boolean;
	isValidType?: boolean;
};

// *************************************************************
// Default Props
// *************************************************************

const defaultProps = {
	acceptedFileTypes: ACCEPTABLE_IMAGE_MIME_TYPES,
	fieldLabel: '',
	setDirtiness: () => {},
	maxFileSizeBytes: MAX_IMAGE_UPLOAD_SIZE, // limiting file size to 20MB
	validateImage: () => null, // Returning null implies that all validation rules were successful
};

// *************************************************************
// The Hook
// *************************************************************

export const useImageUploadField = <TFieldValues extends FieldValues = FieldValues>(
	props: UseImageUploadFieldProps<TFieldValues>
) => {
	const {
		setDirtiness,
		fieldName,
		fieldLabel,
		initialValue,
		maxFileSizeBytes,
		validateImage,
		setImagePreview,
		isDrag,
		imageType,
		isOver,
		isValidType,
	} = {
		...defaultProps,
		...props,
	};

	useEffect(() => {}, [fieldName, initialValue]);

	const [fieldHelperText, setFieldHelperText] = useState<string>('');
	const [errorMessage, setErrorMessage] = useState('');
	const [success, setSuccess] = useState(false);
	const [failure, setFailure] = useState(false);
	const readableFileTypes = ACCEPTABLE_IMAGE_FILE_EXTENSIONS;

	const handleSuccess = () => {
		setSuccess(true);
		setTimeout(() => {
			setSuccess(false);
		}, 3000);
	};

	const handleFailure = () => {
		setFailure(true);
		setTimeout(() => {
			setFailure(false);
		}, 3000);
	};

	const handleErrorMessage = (rejectedFiles: FileRejection[]) => {
		setErrorMessage(
			rejectedFiles.map(
				(rejectedFile) =>
					rejectedFile.errors.map((error) => {
						switch (error.code) {
							case 'file-too-large':
								return `File is too large.`;
							case 'too-many-files':
								return 'Too many files.';
							case 'file-invalid-type':
								return 'Invalid file type.';
							default:
								return error.message;
						}
					})[0]
			)[0]
		);
		setFieldHelperText(
			rejectedFiles.map(
				(rejectedFile) =>
					rejectedFile.errors.map((error) => {
						switch (error.code) {
							case 'file-too-large':
								return `Upload a file that is ${(maxFileSizeBytes / 1000000).toFixed(0)}MB or less.`;
							case 'too-many-files':
								return 'You can only drop 1 image here';
							case 'file-invalid-type':
								let readable = ACCEPTABLE_IMAGE_FILE_EXTENSIONS.split(
									','
								).reduce((total, current, index, arr) =>
									arr[index + 1] ? `${total}, ${current}` : `${total} and ${current}`
								);
								return `The image added isn't a supported format. Only ${readable} image formats can be added.`;
							default:
								return error.message;
						}
					})[0]
			)[0]
		);
	};

	const onDropAccepted: DropzoneOptions['onDropAccepted'] = useCallback(
		async (acceptedFiles) => {
			let imageInfo: Promise<ImageInfo>;
			acceptedFiles.forEach((file) => {
				imageInfo = getImageInfo(file);
				setImagePreview(file);
			});
			const validationErrorMessage = validateImage(await imageInfo);
			setFieldHelperText(validationErrorMessage ?? '');
			setDirtiness(fieldName, true);
			handleSuccess();
		},
		[fieldName, setDirtiness, validateImage, setImagePreview]
	);

	const onDropRejected: DropzoneOptions['onDropRejected'] = (rejectedFiles) => {
		handleFailure();
		handleErrorMessage(rejectedFiles);
	};

	const fieldRequirementsText = (maxFileSizeBytes / 1000000).toFixed(1) + 'MB max';

	const { getRootProps, getInputProps, isDragActive } = useDropzone({
		onDropAccepted,
		onDropRejected,
		accept: ACCEPTABLE_IMAGE_FILE_EXTENSIONS,
		maxFiles: 1,
		maxSize: maxFileSizeBytes,
	});

	const imageUploadFieldProps = {
		getRootProps,
		getInputProps,
		imagePreviewUrl: initialValue,
		isDragActive,
		success,
		failure,
		name: fieldName,
		label: fieldLabel,
		helperText: fieldHelperText,
		errorMessage,
		fieldRequirementsText,
		fileTypes: readableFileTypes,
		isDrag,
		imageType,
		isOver,
		isValidType,
	};

	return [imageUploadFieldProps, ImageUploadField] as const;
};

// *************************************************************
// A lame wrapper component
// *************************************************************

const ImageUploadField = ({
	getRootProps,
	getInputProps,
	fileTypes,
	imagePreviewUrl,
	isDragActive,
	success,
	failure,
	name,
	helperText,
	errorMessage,
	fieldRequirementsText,
	isDrag,
	imageType,
	isOver,
	isValidType,
}: ImageUploadFieldProps) => {
	var stylingProps: { backgroundColor: string; borderColor: string; width: string; height: string };
	const focusedItemName = useAppSelector(_focusedItemName);

	if (success) {
		stylingProps = {
			backgroundColor: colors.greenLight,
			borderColor: colors.greenDark,
			width: '100%',
			height: '183px',
		};
	} else if (failure) {
		stylingProps = {
			backgroundColor: colors.redLight,
			borderColor: colors.redDark,
			width: '100%',
			height: '183px',
		};
	} else if (isOver && isValidType) {
		stylingProps = {
			backgroundColor: colors.tealAccent20,
			borderColor: colors.tealAccent20,
			width: '100%',
			height: '183px',
		};
	} else if ((isDragActive || isDrag) && isValidType) {
		if (imageType === IMAGE_CONTENT) {
			stylingProps = {
				backgroundColor: colors.tealAccent20,
				borderColor: colors.tealAccent20,
				width: '100%',
				height: 'auto',
			};
		} else if (imageType === IMAGE_BANNER) {
			stylingProps = {
				backgroundColor: colors.pureWhite,
				borderColor: colors.tealAccent20,
				width: '100%',
				height: '183px',
			};
		} else if (imageType === IMAGE_PREVIEW) {
			if (focusedItemName === TOPIC_BACKGROUND) {
				stylingProps = {
					backgroundColor: colors.pureWhite,
					borderColor: colors.tealAccent20,
					width: '100%',
					height: '164px',
				};
			} else if (focusedItemName === HEADER_LOGO) {
				stylingProps = {
					backgroundColor: colors.pureWhite,
					borderColor: colors.tealAccent20,
					width: '150px',
					height: '150px',
				};
			} else {
				stylingProps = {
					backgroundColor: colors.pureWhite,
					borderColor: colors.tealAccent20,
					width: '100%',
					height: 'auto',
				};
			}
		}
	} else {
		if (imageType === IMAGE_CONTENT) {
			stylingProps = {
				backgroundColor: colors.pureWhite,
				borderColor: colors.grayLight,
				width: '100%',
				height: 'auto',
			};
		} else if (imageType === IMAGE_BANNER) {
			stylingProps = {
				backgroundColor: colors.grayMedium,
				borderColor: colors.grayLight,
				width: '100%',
				height: '183px',
			};
		} else if (imageType === IMAGE_PREVIEW) {
			if (focusedItemName === TOPIC_BACKGROUND) {
				stylingProps = {
					backgroundColor: colors.grayMedium,
					borderColor: colors.grayLight,
					width: '100%',
					height: '164px',
				};
			} else if (focusedItemName === HEADER_LOGO) {
				stylingProps = {
					backgroundColor: colors.pureWhite,
					borderColor: colors.grayLight,
					width: '150px',
					height: '150px',
				};
			} else {
				stylingProps = {
					backgroundColor: colors.pureWhite,
					borderColor: colors.grayLight,
					width: '100%',
					height: 'auto',
				};
			}
		}
	}

	const classes = useStyles(stylingProps);

	return (
		<div className={classes.dropZone} {...getRootProps()}>
			{success ? (
				<SuccessfulUpload />
			) : failure ? (
				<FailedUpload helperText={helperText} errorMessage={errorMessage} />
			) : (
				<UploadField
					getInputProps={getInputProps}
					imagePreviewUrl={imagePreviewUrl}
					isDragActive={isDragActive || isDrag}
					name={name}
					fieldRequirementsText={fieldRequirementsText}
					imageType={imageType}
					isOver={isOver}
					isValidType={isValidType}
				/>
			)}
		</div>
	);
};

// *************************************************************
// and finally the componet that actually does the work
// *************************************************************

const UploadField = (
	props: Omit<
		ImageUploadFieldProps,
		'getRootProps' | 'success' | 'failure' | 'helperText' | 'errorMessage' | 'label' | 'fileTypes'
	>
) => {
	const { isDragActive, imagePreviewUrl, isOver, imageType, isValidType, getInputProps } = { ...props };

	var styling: { backgroundColor: string; borderColor: string; width: string; height: string };

	const focusedItemName = useAppSelector(_focusedItemName);

	if (isDragActive && isValidType) {
		styling = {
			backgroundColor: colors.tealAccent20,
			borderColor: colors.tealAccent,
			width: '100%',
			height: 'auto',
		};
		if ((imageType === IMAGE_BANNER || imageType === IMAGE_PREVIEW) && !isOver) {
			styling.backgroundColor = colors.pureWhite;
		}
	} else if (isOver && isValidType) {
		styling = {
			backgroundColor: colors.tealAccent20,
			borderColor: colors.tealAccent20,
			width: 'auto',
			height: '183px',
		};
	} else {
		if (imageType === IMAGE_CONTENT) {
			styling = {
				backgroundColor: colors.pureWhite,
				borderColor: colors.grayLight,
				width: '100%',
				height: 'auto',
			};
		} else if (imageType === IMAGE_BANNER) {
			styling = {
				backgroundColor: colors.grayMedium,
				borderColor: colors.grayLight,
				width: '100%',
				height: '183px',
			};
		} else if (imageType === IMAGE_PREVIEW) {
			if (focusedItemName === TOPIC_BACKGROUND) {
				styling = {
					backgroundColor: colors.grayMedium,
					borderColor: colors.grayLight,
					width: '100%',
					height: '164px',
				};
			} else if (focusedItemName === HEADER_LOGO) {
				styling = {
					backgroundColor: colors.pureWhite,
					borderColor: colors.grayLight,
					width: '150px',
					height: '150px',
				};
			} else {
				styling = {
					backgroundColor: colors.pureWhite,
					borderColor: colors.grayLight,
					width: '100%',
					height: 'auto',
				};
			}
		}
	}

	const classes = useStyles(styling);

	return (
		<div
			style={{ width: '100%', height: isOver && isValidType ? '183px' : 'auto' }}
			className={(isDragActive || isOver) && isValidType ? classes.dragState : classes.dropZone}
		>
			{imageType === IMAGE_PREVIEW && <Input inputProps={{ ...getInputProps() }} />}

			{isOver && isValidType ? (
				<>
					<CloudDownloadIcon className={classes.icon} />
					<Typography>Drop to Replace</Typography>
				</>
			) : imagePreviewUrl ? (
				<div className={imageType === IMAGE_BANNER ? classes.wrapper : undefined}>
					<img
						src={imagePreviewUrl}
						alt=""
						className={
							(focusedItemName === TOPIC_BACKGROUND && imageType === IMAGE_PREVIEW) ||
							imageType === IMAGE_BANNER
								? classes.bannerImageOptionContent
								: focusedItemName === HEADER_LOGO && imageType === IMAGE_PREVIEW
								? classes.logoImageOptionContent
								: classes.imageOptionContent
						}
					/>
				</div>
			) : (
				<>
					<Placeholder icon={imageType === IMAGE_BUTTON ? ImageButtonIcon : PlaceImage.default} />
				</>
			)}
		</div>
	);
};

// *************************************************************
// Some helper components
// *************************************************************

const SuccessfulUpload = () => {
	const classes = useStyles({ borderColor: colors.greenDark });
	return (
		<div className={classes.successUpload}>
			<CheckCircleIcon className={classes.icon} />
			<Typography>Uploaded Successfully</Typography>
		</div>
	);
};

const FailedUpload = (props: { helperText: string; errorMessage: string }) => {
	const { helperText, errorMessage } = { ...props };
	const classes = useStyles({ borderColor: colors.redDark });
	return (
		<>
			<WarningIcon className={classes.icon} />
			<Typography>{errorMessage}</Typography>
			<Typography className={classes.failureText}>{helperText}</Typography>
		</>
	);
};
